import React, { useState } from 'react';
import { Input, Typography, Sheet, Stack, Button as JoyButton } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { appendTableViewCustomRecipe } from './list.functions';

/**
 * @param {'compact' | 'panel'} layout — compact: toolbar sotto RecipeSelector; panel: pannello colonne con titolo
 */
const SaveNamedColumnViewControls = ({
    disabled,
    columnOrder,
    visibilityModel,
    tableViewCustomRecipes,
    setSettingsKey,
    layout = 'compact',
    sx: sxProp,
    onSaveSuccess,
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [errorKey, setErrorKey] = useState(null);
    const isPanel = layout === 'panel';

    const handleSave = () => {
        setErrorKey(null);
        const result = appendTableViewCustomRecipe(
            setSettingsKey,
            tableViewCustomRecipes,
            name,
            columnOrder,
            visibilityModel,
        );
        if (result.ok) {
            setName('');
            onSaveSuccess?.();
            return;
        }
        setErrorKey(result.error);
    };

    const canSave = !disabled && Boolean(name.trim());

    return (
        <Sheet
            variant={isPanel ? 'soft' : 'plain'}
            color="neutral"
            sx={[
                {
                    borderRadius: 'md',
                    ...(isPanel
                        ? {
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.level1',
                          }
                        : {
                              mt: 1.25,
                              pt: 1.25,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                          }),
                },
                ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
            ]}
        >
            {isPanel && (
                <Typography level="title-sm" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {t('save_column_view_section')}
                </Typography>
            )}
            {isPanel && (
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                    {t('save_column_view_hint')}
                </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'stretch' }}>
                <Input
                    size="sm"
                    value={name}
                    disabled={disabled}
                    onChange={(e) => {
                        setName(e.target.value);
                        setErrorKey(null);
                    }}
                    placeholder={t('save_column_view_name_placeholder')}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        bgcolor: 'background.surface',
                    }}
                    className="form-element-field"
                    autoComplete="off"
                />
                <JoyButton
                    type="button"
                    size="sm"
                    variant="solid"
                    className="input-button small"
                    disabled={!canSave}
                    onClick={handleSave}
                    sx={{
                        minWidth: { sm: 128 },
                        alignSelf: { xs: 'stretch', sm: 'auto' },
                        fontWeight: 600,
                        bgcolor: 'var(--orange-500) !important',
                        color: '#fff !important',
                        '&:hover': {
                            bgcolor: 'var(--orange-600) !important',
                        },
                        '&:active': {
                            bgcolor: 'var(--orange-700) !important',
                        },
                        '&:disabled, &.Mui-disabled': {
                            bgcolor: 'var(--joy-palette-neutral-200, #e0e0e0) !important',
                            color: 'var(--joy-palette-neutral-500, #888) !important',
                        },
                    }}
                >
                    {t('save_column_view')}
                </JoyButton>
            </Stack>
            {errorKey === 'empty_name' && (
                <Typography level="body-xs" sx={{ mt: 1, color: 'danger.500' }}>
                    {t('save_column_view_error_empty')}
                </Typography>
            )}
            {errorKey === 'duplicate_name' && (
                <Typography level="body-xs" sx={{ mt: 1, color: 'danger.500' }}>
                    {t('save_column_view_error_duplicate')}
                </Typography>
            )}
        </Sheet>
    );
};

export default SaveNamedColumnViewControls;
